import React, { useState } from 'react'
import { format } from 'date-fns'
import { nb } from 'date-fns/locale'
import { Plus, Repeat, Trash2 } from 'lucide-react'
import { apiDelete, apiPost, apiPut } from '../../api'
import { Alert, Button, Field, IconButton, Modal, StatusPill } from '../../components/ui'
import SlotForm from './SlotForm'
import { InlineConfirm, Swatch, fmtPrice, fmtTime, formToPayload, readError, slotToForm } from './shared'

/** Kilde-etikett for en booking (samme ordvalg som før, nå som statuspille). */
function bookingSource(b) {
  if (b.status === 'PENDING_PAYMENT') return { label: 'Betaler nå', tone: 'signal' }
  if (b.source === 'APP') return b.amountOre > 0 ? { label: `Betalt ${fmtPrice(b.amountOre)}`, tone: 'open' } : { label: 'Booket i appen', tone: 'outline' }
  if (b.source === 'AGREEMENT') return { label: 'Fast avtale', tone: 'live' }
  return { label: 'Lagt til av arena', tone: 'neutral' }
}

/** Detaljer for en utleie: rediger, se bookinger, legg til person, avlys. */
function SlotDetail({ entry, courts, facilityId, onChanged, onClose, courtStyle }) {
  const { slot, bookings } = entry
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [person, setPerson] = useState({ phone: '', name: '' })
  const [confirming, setConfirming] = useState(null) // booking-id eller 'cancel'
  const isAgreement = Boolean(slot.agreementId)

  const run = async (fn) => {
    setSaving(true)
    setError('')
    try {
      await fn()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const save = (form) =>
    run(async () => {
      const payload = formToPayload(form)
      if (!payload.courtId) payload.clearCourt = true
      const res = await apiPut(`/api/facilities/${facilityId}/rentals/${slot.id}`, payload)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre utleien'))
      onChanged(await res.json())
    })

  const addPerson = (e) => {
    e.preventDefault()
    run(async () => {
      const res = await apiPost(`/api/facilities/${facilityId}/rentals/${slot.id}/bookings`, {
        phone: person.phone.trim() || null,
        name: person.name.trim() || null
      })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke legge til personen'))
      setPerson({ phone: '', name: '' })
      onChanged(await res.json())
    })
  }

  // Bekreftes i raden: «Fjerne bookingen til …?»
  const removeBooking = (b) => {
    setConfirming(null)
    run(async () => {
      const res = await apiDelete(`/api/facilities/${facilityId}/rentals/${slot.id}/bookings/${b.id}?refund=true`)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke fjerne bookingen'))
      onChanged(await res.json())
    })
  }

  // Bekreftes i bunnlinjen: «Avlyse utleien? …»
  const cancelSlot = () => {
    setConfirming(null)
    run(async () => {
      const res = await apiDelete(`/api/facilities/${facilityId}/rentals/${slot.id}`)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke avlyse utleien'))
      onChanged(null)
      onClose()
    })
  }

  const start = new Date(slot.startsAt)
  const end = new Date(slot.endsAt)
  const subtitle = (
    <span className="mono">
      {format(start, 'EEE d. MMM', { locale: nb })} · {fmtTime(start)}–{fmtTime(end)}
      {slot.courtName ? ` · ${slot.courtName}` : ''}
    </span>
  )

  return (
    <Modal
      title={<span className="rt-modal-title"><Swatch style={courtStyle(slot.courtId)} />{slot.title}</span>}
      subtitle={subtitle}
      onClose={onClose}
      size="rt-detail-modal"
    >
      {isAgreement && (
        <div className="alert alert-info" role="note">
          <Repeat size={18} strokeWidth={1.9} aria-hidden="true" />
          <div>Del av en fast avtale. Endringer gjelder bare denne gangen.</div>
        </div>
      )}

      <div className="rt-detail">
        <div className="rt-detail-main">
          <SlotForm initial={slotToForm(slot)} courts={courts} onSubmit={save} submitting={saving} submitLabel="Lagre endringer" />
        </div>

        <div className="rt-detail-side">
          <section className="rt-bookings" aria-labelledby="rt-bk-h">
            <h3 id="rt-bk-h" className="h3">Bookinger ({slot.bookedCount}/{slot.capacity})</h3>
            {bookings.length === 0 ? (
              <p className="muted rt-small">Ingen har booket ennå.</p>
            ) : (
              <ul className="rt-booking-list">
                {bookings.map((b) => {
                  const src = bookingSource(b)
                  const paid = b.amountOre > 0
                  return (
                    <li key={b.id} className="rt-booking">
                      <div className="rt-booking-row">
                        <div className="rt-booking-main">
                          <span className="rt-booking-name">{b.displayName}</span>
                          {b.phone && <span className="rt-booking-phone mono">{b.phone}</span>}
                        </div>
                        <StatusPill tone={src.tone}>{src.label}</StatusPill>
                        <IconButton
                          icon={Trash2}
                          label={`Fjern bookingen til ${b.displayName}`}
                          size="sm"
                          danger
                          iconSize={16}
                          onClick={() => setConfirming(b.id)}
                          disabled={saving}
                        />
                      </div>
                      {confirming === b.id && (
                        <InlineConfirm
                          label="Bekreft fjerning"
                          className="rt-confirm-flat"
                          message={`Fjerne bookingen til ${b.displayName}?${paid ? ' Betalingen refunderes.' : ''}`}
                          confirmLabel={paid ? 'Fjern og refunder' : 'Fjern'}
                          onConfirm={() => removeBooking(b)}
                          onCancel={() => setConfirming(null)}
                          busy={saving}
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {slot.spotsLeft > 0 && (
            <form className="rt-add-person" onSubmit={addPerson} aria-labelledby="rt-bp-h">
              <h3 id="rt-bp-h" className="h3">Book for en person</h3>
              <Field label="Telefon (finner brukeren)">
                <input
                  type="tel"
                  inputMode="tel"
                  placeholder="912 34 567"
                  value={person.phone}
                  onChange={(e) => setPerson((p) => ({ ...p, phone: e.target.value }))}
                />
              </Field>
              <Field label="Navn (hvis uten app)">
                <input
                  placeholder="F.eks. Per Hansen"
                  value={person.name}
                  onChange={(e) => setPerson((p) => ({ ...p, name: e.target.value }))}
                />
              </Field>
              <Button
                type="submit"
                variant="secondary"
                icon={Plus}
                disabled={saving || (!person.phone.trim() && !person.name.trim())}
              >
                Legg til
              </Button>
            </form>
          )}

          {error && <Alert tone="error">{error}</Alert>}
        </div>
      </div>

      <div className="rt-detail-foot">
        {confirming === 'cancel' ? (
          <InlineConfirm
            label="Bekreft avlysning"
            className="rt-confirm-danger"
            message="Avlyse utleien? Alle som har booket varsles, og betalinger refunderes."
            confirmLabel="Avlys utleie"
            onConfirm={cancelSlot}
            onCancel={() => setConfirming(null)}
            busy={saving}
          />
        ) : (
          <Button variant="danger-soft" onClick={() => setConfirming('cancel')} disabled={saving}>Avlys utleie</Button>
        )}
      </div>
    </Modal>
  )
}

export default SlotDetail
